import { useNavigate, useLocation } from "react-router-dom";
import { buildAuthUrl } from "@/lib/authRedirect";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { useHeaderProfile, useIsArenaOrProfessor } from "@/hooks/useHeaderUserData";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Home, 
  Trophy, 
  ClipboardList, 
  Menu,
  Heart,
  Building2,
  DollarSign,
  Calendar,
  LogOut,
  User,
  LogIn,
  Search,
  Settings,
  Shield,
  Ticket,
  ChevronRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { hapticLight } from "@/lib/capacitor";

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, hasRole } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  // Cache compartilhado com Header (5min) — antes o useEffect refazia
  // ambas queries a cada mount, gerando 4-6 requests duplicados por navegação.
  const { data: userProfile } = useHeaderProfile();
  const { data: isArenaOrProfessor = false } = useIsArenaOrProfessor();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    setMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    hapticLight();
    // Preserva a página atual ao mandar para login a partir do menu mobile
    if (path === "/autenticacao") {
      navigate(buildAuthUrl());
    } else {
      navigate(path);
    }
    setMenuOpen(false);
  };

  const isOrganizer = hasRole('organizador');
  const isAthlete = hasRole('atleta');
  const isAdmin = hasRole('admin');
  const showAthleteMenu = isAthlete || (user && !isOrganizer && !isArenaOrProfessor);

  const isActive = (path: string) => location.pathname === path;

  // Define main nav items based on user role.
  // "Jogos" aponta para /torneios-publicos (visão pública de torneios/jogos abertos).
  const getMainNavItems = () => {
    if (!user) {
      return [
        { icon: Home, label: "Início", path: "/" },
        { icon: Calendar, label: "Jogos", path: "/torneios-publicos" },
        { icon: LogIn, label: "Entrar", path: "/autenticacao" },
      ];
    }

    const items = [
      { icon: Home, label: "Painel", path: "/painel" },
      { icon: Calendar, label: "Jogos", path: "/torneios-publicos" },
    ];

    // Arena users always see Arenas; organizers always see Torneios
    if (isOrganizer || isArenaOrProfessor) {
      items.push({ icon: Building2, label: "Arenas", path: "/minhas-arenas" });
    }

    if (isOrganizer) {
      items.push({ icon: Trophy, label: "Torneios", path: "/torneios" });
    }

    // Portal do Atleta sempre disponível para qualquer usuário autenticado
    items.push({ icon: ClipboardList, label: "Atleta", path: "/atleta" });

    return items;
  };

  const mainNavItems = getMainNavItems();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const MenuSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="space-y-1">
      <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {title}
      </div>
      {children}
    </div>
  );

  const MenuItem = ({ 
    icon: Icon, 
    label, 
    path, 
    variant = "ghost",
    className = ""
  }: { 
    icon: typeof Home; 
    label: string; 
    path: string;
    variant?: "ghost" | "default" | "destructive";
    className?: string;
  }) => (
    <Button
      variant={variant}
      className={cn(
        "justify-between w-full h-12 text-base font-normal",
        isActive(path) && "bg-primary/10 text-primary font-medium",
        className
      )}
      onClick={() => handleNavigation(path)}
    >
      <span className="flex items-center">
        <Icon className="mr-3 h-5 w-5" />
        {label}
      </span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Button>
  );

  return (
    <nav className="mobile-bottom-nav lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur-lg supports-[backdrop-filter]:bg-background/80 safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-1">
        {mainNavItems.map((item) => (
          <button
            key={item.path + item.label}
            onClick={() => { hapticLight(); navigate(item.path === "/autenticacao" ? buildAuthUrl() : item.path); }}
            className={cn(
              "flex flex-col items-center justify-center gap-0.5 py-2 px-3 rounded-xl transition-all duration-200 touch-manipulation min-w-[56px] active:scale-95",
              isActive(item.path) 
                ? "text-primary" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <div className={cn(
              "p-1.5 rounded-lg transition-colors",
              isActive(item.path) && "bg-primary/15"
            )}>
              <item.icon className={cn(
                "h-5 w-5 transition-transform",
                isActive(item.path) && "scale-110"
              )} />
            </div>
            <span className={cn(
              "text-[10px] transition-all",
              isActive(item.path) ? "font-semibold" : "font-medium"
            )}>
              {item.label}
            </span>
          </button>
        ))}

        {/* Menu Button */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 py-2 px-3 rounded-xl transition-all duration-200 touch-manipulation min-w-[56px] active:scale-95",
                menuOpen 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className={cn(
                "p-1.5 rounded-lg transition-colors",
                menuOpen && "bg-primary/15"
              )}>
                <Menu className="h-5 w-5" />
              </div>
              <span className="text-[10px] font-medium">Menu</span>
            </button>
          </SheetTrigger>
          <SheetContent 
            side="bottom" 
            className="h-auto max-h-[85vh] rounded-t-2xl px-4 pb-8"
          >
            <SheetHeader className="pb-2">
              <div className="mx-auto w-12 h-1 rounded-full bg-muted mb-4" />
              <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
            </SheetHeader>

            <div className="space-y-4 overflow-y-auto">
              {/* User Profile Card */}
              {user && userProfile && (
                <div 
                  className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => handleNavigation("/perfil")}
                >
                  <Avatar className="h-12 w-12 border-2 border-primary/20">
                    <AvatarImage src={userProfile.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {getInitials(userProfile.full_name || user.email || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {userProfile.full_name || 'Meu Perfil'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              )}

              {/* Quick Actions for Authenticated Users */}
              {user && (
                <div className="grid grid-cols-4 gap-2">
                  {isOrganizer && (
                    <button
                      onClick={() => handleNavigation("/painel-organizador")}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary/10">
                        <Trophy className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-[10px] font-medium text-center">Painel</span>
                    </button>
                  )}
                  {isArenaOrProfessor && (
                    <button
                      onClick={() => handleNavigation("/minhas-arenas")}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary/10">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-[10px] font-medium text-center">Arenas</span>
                    </button>
                  )}
                  {/* Portal do Atleta — sempre disponível para usuários autenticados */}
                  <button
                    onClick={() => handleNavigation("/atleta")}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="p-2 rounded-full bg-primary/10">
                      <ClipboardList className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-[10px] font-medium text-center">Atleta</span>
                  </button>
                  <button
                    onClick={() => handleNavigation("/favoritos")}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="p-2 rounded-full bg-primary/10">
                      <Heart className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-[10px] font-medium text-center">Favoritos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation("/perfil")}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="p-2 rounded-full bg-primary/10">
                      <Settings className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-[10px] font-medium text-center">Config</span>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleNavigation("/admin")}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="p-2 rounded-full bg-destructive/10">
                        <Shield className="h-5 w-5 text-destructive" />
                      </div>
                      <span className="text-[10px] font-medium text-center">Admin</span>
                    </button>
                  )}
                </div>
              )}

              {/* Soluções Section - apenas para visitantes não autenticados */}
              {!user && (
                <MenuSection title="Soluções">
                  <MenuItem icon={Building2} label="Gestão de Arenas" path="/solucao/arenas" />
                  <MenuItem icon={Trophy} label="Gestão de Torneios" path="/solucao/torneios" />
                  <MenuItem icon={DollarSign} label="ERP Financeiro" path="/solucao/erp-financeiro" />
                  <MenuItem icon={Calendar} label="Portal do Atleta" path="/solucao/portal-atleta" />
                </MenuSection>
              )}

              {/* User Actions */}
              {user && (
                <MenuSection title="Minha Conta">
                  <MenuItem icon={User} label="Meu Perfil" path="/perfil" />
                  <MenuItem icon={ClipboardList} label="Portal do Atleta" path="/atleta" />
                  <MenuItem icon={Ticket} label="Minhas Inscrições" path="/minhas-inscricoes" />
                  {isOrganizer && (
                    <MenuItem icon={Trophy} label="Meus Torneios" path="/torneios" />
                  )}
                  <Button
                    variant="ghost"
                    className="justify-between w-full h-12 text-base font-normal text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleSignOut}
                  >
                    <span className="flex items-center">
                      <LogOut className="mr-3 h-5 w-5" />
                      Sair da conta
                    </span>
                  </Button>
                </MenuSection>
              )}

              {/* Login for unauthenticated */}
              {!user && (
                <div className="pt-2">
                  <Button
                    variant="default"
                    className="w-full h-12 text-base font-semibold"
                    onClick={() => handleNavigation("/autenticacao")}
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Entrar ou Criar Conta
                  </Button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};
